import { createFeatureSelector, createReducer, on } from '@ngrx/store';

import { createScopedSelector } from '@hunting-nature/Secacam/shared/utils/factory.utils';

import { LiveStreamActions } from './live-stream.actions';

export const LIVE_STREAM_STORE_KEY = 'liveStream';

export type LiveStreamStatus = 'idle' | 'starting' | 'streaming' | 'error';

/** The capture operation a piece of state refers to: the error sheet names the failed one, the confirmation badge the successful one. */
export type CaptureType = 'screenshot' | 'recording';

/** Content of the blocking capture-error bottom sheet in the viewer (recording/screenshot failed). */
export interface CaptureErrorState {
  type: CaptureType;
  error: string;
}

// state
export interface LiveStreamState {
  isBottomSheetVisible: boolean;
  streamStatus: LiveStreamStatus;
  error: string | undefined;
  /** RTCPeerConnection state — 'failed' after signaling means the stream died mid-watch. */
  connectionState: string | undefined;
  /** Viewer-level pause: the media session is torn down, the modal stays open until resume. */
  isPaused: boolean;
  /** True while a pause/resume media-session transition is still running (controls blocked). */
  isStreamTransitioning: boolean;
  /** Result-driven: true only after recordVideoSucceeded({recording: true}) — a failed stop keeps it true. */
  isRecording: boolean;
  /** True while a RecordVideo BLE write is in flight (record/stop button blocked). */
  isRecordPending: boolean;
  /** True while a TakePicture BLE write is in flight (screenshot button blocked). */
  isTakePicturePending: boolean;
  /** Transient confirmation badge after a successful capture; cleared on dismiss/viewer close. */
  captureConfirmation: CaptureType | undefined;
  /** Set while the blocking capture-error sheet is visible (recording/screenshot failed). */
  captureError: CaptureErrorState | undefined;
  /** Set while the 'stop the recording before leaving?' confirm sheet is visible. */
  isStopRecordingConfirmVisible: boolean;
}

export const initialLiveStreamState: LiveStreamState = {
  isBottomSheetVisible: false,
  streamStatus: 'idle',
  error: undefined,
  connectionState: undefined,
  isPaused: false,
  isStreamTransitioning: false,
  isRecording: false,
  isRecordPending: false,
  isTakePicturePending: false,
  captureConfirmation: undefined,
  captureError: undefined,
  isStopRecordingConfirmVisible: false,
};

const createLiveStreamSelector = createScopedSelector(createFeatureSelector<LiveStreamState>(LIVE_STREAM_STORE_KEY));

export const liveStreamReducer = createReducer(
  initialLiveStreamState,
  on(
    LiveStreamActions.openLiveStream,
    (state): LiveStreamState =>
      // Re-entry while streaming (double-tap, sheet re-trigger) must not reset the viewer to
      // 'starting' — that closes the already-running stream right after its first frame.
      state.streamStatus === 'streaming'
        ? state
        : {
            ...state,
            isBottomSheetVisible: true,
            streamStatus: 'starting',
            error: undefined,
            connectionState: undefined,
            isPaused: false,
            isStreamTransitioning: false,
          }
  ),
  on(
    LiveStreamActions.openLiveStreamSucceeded,
    (state): LiveStreamState => ({
      ...state,
      streamStatus: 'streaming',
    })
  ),
  on(
    LiveStreamActions.openLiveStreamFailed,
    (state, { error }): LiveStreamState => ({
      ...state,
      streamStatus: 'error',
      error,
    })
  ),
  on(
    LiveStreamActions.closeLiveStream,
    (state): LiveStreamState => ({
      ...state,
      isBottomSheetVisible: false,
    })
  ),
  on(
    LiveStreamActions.closeLiveStreamSucceeded,
    (state): LiveStreamState => ({
      ...state,
      streamStatus: 'idle',
      error: undefined,
      connectionState: undefined,
      isPaused: false,
      isStreamTransitioning: false,
      isRecording: false,
      isRecordPending: false,
      isTakePicturePending: false,
      captureConfirmation: undefined,
      isStopRecordingConfirmVisible: false,
      captureError: undefined,
    })
  ),
  // Pausing only tears the media session down — the modal stays open, so visibility and the
  // streaming status are left untouched. The intent only blocks the controls (spinner); the
  // paused UI appears on *Succeeded, a failure keeps the stream live (button re-enables).
  on(
    LiveStreamActions.pauseStream,
    (state): LiveStreamState => ({
      ...state,
      isStreamTransitioning: true,
    })
  ),
  on(
    LiveStreamActions.pauseStreamSucceeded,
    (state): LiveStreamState => ({
      ...state,
      isPaused: true,
      isStreamTransitioning: false,
    })
  ),
  // A failed pause changes nothing visible: the stream stays live, the button re-enables.
  on(
    LiveStreamActions.pauseStreamFailed,
    (state): LiveStreamState => ({
      ...state,
      isStreamTransitioning: false,
    })
  ),
  on(
    LiveStreamActions.resumeStream,
    (state): LiveStreamState => ({
      ...state,
      isPaused: true,
      isStreamTransitioning: true,
      error: undefined,
    })
  ),
  on(
    LiveStreamActions.resumeStreamSucceeded,
    (state): LiveStreamState => ({
      ...state,
      isPaused: false,
      isStreamTransitioning: false,
    })
  ),
  on(
    LiveStreamActions.resumeStreamFailed,
    (state, { error }): LiveStreamState => ({
      ...state,
      isPaused: true,
      isStreamTransitioning: false,
      error,
    })
  ),
  // Recording is result-driven: the intent only marks the request as pending (button blocked,
  // indicator unchanged); *Succeeded/*Failed apply the outcome. A failed stop therefore keeps
  // the recording UI alive, matching the device's actual state.
  on(
    LiveStreamActions.recordVideo,
    (state): LiveStreamState => ({
      ...state,
      isRecordPending: true,
    })
  ),
  on(
    LiveStreamActions.recordVideoSucceeded,
    (state, { recording }): LiveStreamState => ({
      ...state,
      isRecording: recording,
      isRecordPending: false,
      // A confirmed start needs no badge — the recording indicator (timer + button change) is the confirmation
      captureConfirmation: recording ? undefined : 'recording',
      captureError: undefined,
    })
  ),
  // The *Failed actions ARE the error-sheet trigger: the pending flag clears and the blocking
  // sheet opens in the same transition. isRecording is NOT touched: a failed stop keeps the
  // recording active (the device never confirmed the stop), a failed start was never active.
  // One shared message covers both directions — the operation that failed is the toggle.
  on(
    LiveStreamActions.recordVideoFailed,
    (state, { error }): LiveStreamState => ({
      ...state,
      isRecordPending: false,
      captureError: { type: 'recording', error },
    })
  ),
  on(
    LiveStreamActions.takePicture,
    (state): LiveStreamState => ({
      ...state,
      isTakePicturePending: true,
    })
  ),
  on(
    LiveStreamActions.takePictureSucceeded,
    (state): LiveStreamState => ({
      ...state,
      isTakePicturePending: false,
      captureConfirmation: 'screenshot',
      captureError: undefined,
    })
  ),
  on(
    LiveStreamActions.takePictureFailed,
    (state, { error }): LiveStreamState => ({
      ...state,
      isTakePicturePending: false,
      captureError: { type: 'screenshot', error },
    })
  ),
  on(
    LiveStreamActions.dismissCaptureConfirmation,
    (state): LiveStreamState => ({
      ...state,
      captureConfirmation: undefined,
    })
  ),
  on(
    LiveStreamActions.dismissCaptureError,
    (state): LiveStreamState => ({
      ...state,
      captureError: undefined,
    })
  ),
  on(
    LiveStreamActions.showStopRecordingConfirm,
    (state): LiveStreamState => ({
      ...state,
      isStopRecordingConfirmVisible: true,
    })
  ),
  on(
    LiveStreamActions.dismissStopRecordingConfirm,
    (state): LiveStreamState => ({
      ...state,
      isStopRecordingConfirmVisible: false,
    })
  ),
  on(
    LiveStreamActions.connectionStateChanged,
    (state, { connectionState }): LiveStreamState => ({
      ...state,
      connectionState,
    })
  ),
  // A stream that connected but never delivered a frame is dead — but the viewer modal stays
  // open: the media session is already torn down and the state reads as "paused", so the play
  // button doubles as the retry (it dispatches resumeStream, replaying the full start
  // sequence). Closing the viewer here would discard the whole WiFi + BLE + AP context the
  // user just set up.
  on(
    LiveStreamActions.videoTimedOut,
    (state): LiveStreamState =>
      state.streamStatus === 'streaming'
        ? {
            ...state,
            isPaused: true,
            isStreamTransitioning: false,
            connectionState: 'failed',
          }
        : state
  )
);

export const LiveStreamSelectors = {
  isBottomSheetVisible: createLiveStreamSelector((state: LiveStreamState) => state.isBottomSheetVisible),
  streamStatus: createLiveStreamSelector((state: LiveStreamState) => state.streamStatus),
  error: createLiveStreamSelector((state: LiveStreamState) => state.error),
  connectionState: createLiveStreamSelector((state: LiveStreamState) => state.connectionState),
  isPaused: createLiveStreamSelector((state: LiveStreamState) => state.isPaused),
  isStreamTransitioning: createLiveStreamSelector((state: LiveStreamState) => state.isStreamTransitioning),
  isRecording: createLiveStreamSelector((state: LiveStreamState) => state.isRecording),
  isRecordPending: createLiveStreamSelector((state: LiveStreamState) => state.isRecordPending),
  isTakePicturePending: createLiveStreamSelector((state: LiveStreamState) => state.isTakePicturePending),
  captureConfirmation: createLiveStreamSelector((state: LiveStreamState) => state.captureConfirmation),
  captureError: createLiveStreamSelector((state: LiveStreamState) => state.captureError),
  isStopRecordingConfirmVisible: createLiveStreamSelector((state: LiveStreamState) => state.isStopRecordingConfirmVisible),
  isViewerVisible: createLiveStreamSelector((state: LiveStreamState) => state.isBottomSheetVisible && state.streamStatus === 'streaming'),
};
